import {
    useContext,
    Children,
    useMemo,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ITableRowProps from "./type";
import type {
    TableRowContentProps,
    TableRowCellProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    TableSectionContext,
    TableRowContext,
    useTableContext
} from "../../context";
import StateLayer from "../../../stateLayer";

const TableRowCell: FC<TableRowCellProps> = ({
    columnIndex,
    isDisabled,
    isSelected,
    isHovered,
    isPressed,
    children
}) => {
    const value = useMemo(() => ({
        columnIndex,
        isDisabled,
        isHovered,
        isPressed,
        isSelected
    }), [
        columnIndex,
        isDisabled,
        isSelected,
        isHovered,
        isPressed
    ]);

    return <TableRowContext.Provider
        value={value}
    >
        {children}
    </TableRowContext.Provider>;
};

const TableRowContent: FC<TableRowContentProps> = ({
    customTheme: customThemeProp,
    isDisabled = false,
    isSelected = false,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    children,
    context,
    onFocus,
    onPress,
    onBlur,
    style,
    ...props
}) => {
    const {
        section,
        isLast
    } = useContext(TableSectionContext);

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customThemeProp ?? context.customTheme);

    const isPressable = !!onPress && !isDisabled;

    const isInteractive = section === "body" && !isDisabled;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: isDisabled || section !== "body",
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible: isFocusVisible && isPressable,
        measures: context.measures,
        isPressable,
        isSelected,
        borders,
        section,
        colors,
        isLast
    });

    const currentIsHovered = isInteractive && isHovered && Platform.OS === "web";

    const currentIsPressed = isPressable && isPressed;

    const cells = Children.toArray(children);

    return <Pressable
        {...props}
        {...interactionHandlers}
        aria-selected={isSelected ? true : undefined}
        tabIndex={isPressable ? 0 : -1}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        accessible={isPressable}
        focusable={isPressable}
        disabled={isDisabled}
        onPress={onPress}
        role="row"
    >
        <View
            style={[
                style,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                customTheme={customThemeProp ?? context.customTheme}
                isHovered={currentIsHovered}
                isPressed={currentIsPressed}
                overlayColor="default"
                borderRadius={0}
            />
            {
                cells.map((cell, index) => <TableRowCell
                    isHovered={currentIsHovered}
                    isPressed={currentIsPressed}
                    isDisabled={isDisabled}
                    isSelected={isSelected}
                    columnIndex={index}
                    key={index}
                >
                    {cell}
                </TableRowCell>)
            }
        </View>
    </Pressable>;
};

const TableRow: FC<ITableRowProps> = (props) => {
    const context = useTableContext({
        part: "Row"
    });

    if(!context) {
        return null;
    }

    return <TableRowContent
        {...props}
        context={context}
    />;
};
export default TableRow;
